import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
	adminUsersApi,
	type CreateUserPayload,
	type UpdateUserPayload,
	type UsersListParams,
} from "@/lib/admin/admin-users-api";

export const useAdminUsersList = (params: UsersListParams) =>
	useQuery({
		queryKey: ["admin", "users", params],
		queryFn: () => adminUsersApi.list(params),
		staleTime: 10_000,
	});

export const useAdminRolesPermissions = () =>
	useQuery({
		queryKey: ["admin", "roles-permissions"],
		queryFn: adminUsersApi.rolesPermissions,
		staleTime: 5 * 60_000,
	});

export const useAdminUser = (id: string | undefined) =>
	useQuery({
		queryKey: ["admin", "users", id],
		queryFn: () => adminUsersApi.get(id!),
		enabled: !!id,
	});

export const useAdminUserSessions = (id: string | undefined) =>
	useQuery({
		queryKey: ["admin", "users", id, "sessions"],
		queryFn: () => adminUsersApi.sessions(id!),
		enabled: !!id,
	});

export const useAdminUserAudit = (id: string | undefined) =>
	useQuery({
		queryKey: ["admin", "users", id, "audit"],
		queryFn: () => adminUsersApi.audit(id!),
		enabled: !!id,
	});

export const useAdminUserMfa = (id: string | undefined) =>
	useQuery({
		queryKey: ["admin", "users", id, "mfa"],
		queryFn: () => adminUsersApi.mfa(id!),
		enabled: !!id,
	});

export const useCreateAdminUser = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (payload: CreateUserPayload) => adminUsersApi.create(payload),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["admin", "users"] });
		},
	});
};

export const useUpdateAdminUser = (id: string) => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (payload: UpdateUserPayload) =>
			adminUsersApi.update(id, payload),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["admin", "users"] });
		},
	});
};

type IdAction = (id: string) => Promise<unknown>;
function useUserAction(action: IdAction) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => action(id),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
	});
}

export const useSuspendUser = () => useUserAction(adminUsersApi.suspend);
export const useReactivateUser = () => useUserAction(adminUsersApi.reactivate);
export const useArchiveUser = () => useUserAction(adminUsersApi.archive);
export const useResetMfaUser = () => useUserAction(adminUsersApi.resetMfa);
export const useForceLogoutUser = () =>
	useUserAction(adminUsersApi.forceLogout);
